import React, { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Pressable, View } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import styled from 'styled-components/native';
import { REDESIGN_COLORS } from '../../styles/colors';
import BetaFooter from './BetaFooter';
import ReportDraftModal from './ReportDraftModal';
import { apiService } from '../../services/apiService';
import logger from '../../utils/logger';
import { stripQuotedReply } from '../../utils/negotiation/stripQuotedReply';

// ---------- Role configuration ----------

const ROLE_CONFIG = {
  dealer_received: {
    align: 'flex-start',
    bgColor: '#2A3140',
    label: 'Dealer',
    labelColor: REDESIGN_COLORS.scoreMedium,
    textColor: REDESIGN_COLORS.textPrimary,
    maxWidthPct: '85%',
  },
  agent_draft: {
    align: 'flex-end',
    bgColor: 'rgba(5, 86, 247, 0.15)',
    label: 'AI Draft',
    labelColor: REDESIGN_COLORS.ctaBlue,
    textColor: REDESIGN_COLORS.textPrimary,
    maxWidthPct: '85%',
  },
  agent_sent: {
    align: 'flex-end',
    bgColor: 'rgba(5, 86, 247, 0.25)',
    label: 'You',
    labelColor: REDESIGN_COLORS.ctaBlue,
    textColor: REDESIGN_COLORS.textPrimary,
    maxWidthPct: '85%',
  },
  user_written: {
    align: 'flex-end',
    bgColor: 'rgba(5, 86, 247, 0.20)',
    label: 'You',
    labelColor: REDESIGN_COLORS.ctaBlue,
    textColor: REDESIGN_COLORS.textPrimary,
    maxWidthPct: '85%',
  },
  dealer_reported: {
    align: 'flex-start',
    bgColor: '#2A3140',
    label: 'Dealer response',
    labelColor: REDESIGN_COLORS.scoreMedium,
    textColor: REDESIGN_COLORS.textPrimary,
    maxWidthPct: '85%',
  },
  system: {
    align: 'center',
    bgColor: 'rgba(255, 255, 255, 0.06)',
    label: 'DooCars',
    labelColor: REDESIGN_COLORS.textSecondary,
    textColor: REDESIGN_COLORS.textSecondary,
    maxWidthPct: '90%',
  },
};

// Roles that should display the dealer's actual name when available.
const DEALER_ROLES = new Set(['dealer_received', 'dealer_reported']);

const DEFAULT_CONFIG = ROLE_CONFIG.system;

// ---------- Helpers ----------

/**
 * Format a timestamp into a human-readable short string.
 * Uses relative time for recent messages and date for older ones.
 */
const formatTimestamp = (isoString) => {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMinutes = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;

    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
};

/**
 * Get the status icon name for the message status.
 */
const getStatusIcon = (status) => {
  switch (status) {
    case 'sent':
      return 'check';
    case 'delivered':
      return 'check-all';
    case 'draft':
      return 'pencil-outline';
    case 'generating':
      return 'dots-horizontal';
    case 'approved':
      return 'check-circle-outline';
    case 'send_failed':
      return 'alert-circle-outline';
    case 'retry_pending':
      return 'reload';
    default:
      return null;
  }
};

// ---------- Styled components ----------

const BubbleRow = styled.View`
  flex-direction: row;
  justify-content: ${(props) => {
    if (props.$align === 'flex-end') return 'flex-end';
    if (props.$align === 'center') return 'center';
    return 'flex-start';
  }};
  margin-bottom: 12px;
`;

const BubbleContainer = styled.View`
  max-width: ${(props) => props.$maxWidth || '85%'};
  background-color: ${(props) => props.$bgColor || '#2A3140'};
  border-radius: 12px;
  padding: 10px 14px;
`;

const RoleLabel = styled.Text`
  font-family: Satoshi-Bold;
  font-size: 11px;
  color: ${(props) => props.$color || REDESIGN_COLORS.textSecondary};
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const MessageContent = styled.Text`
  font-family: Satoshi-Regular;
  font-size: 14px;
  line-height: 20px;
  color: ${(props) => props.$color || REDESIGN_COLORS.textPrimary};
`;

const MetaRow = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  margin-top: 6px;
  gap: 4px;
`;

const TimestampText = styled.Text`
  font-family: Satoshi-Regular;
  font-size: 11px;
  color: ${REDESIGN_COLORS.textSecondary};
`;

const SectionHeader = styled.Text`
  font-family: Satoshi-Bold;
  font-size: 13px;
  color: ${REDESIGN_COLORS.ctaBlue};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
  margin-top: 4px;
`;

// ---------- Component ----------

// AttachmentChip — clickable file chip. Talks to the
// negotiation-attachment-download edge function on tap, then triggers a
// browser download via an object URL. Only dealer messages produce
// downloadable attachments today (worker stamp); we still render a chip for
// other roles, just disabled.
const AttachmentChip = ({ message, att, idx }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const sizeKb = att?.size ? Math.max(1, Math.round(att.size / 1024)) : null;
  const filename = att?.filename || 'attachment';
  const aiRead = typeof att?.extractedText === 'string' && att.extractedText.length > 0;
  const canDownload = message.role === 'dealer_received' && !!message.id;

  const handlePress = async () => {
    if (!canDownload || busy) return;
    setBusy(true);
    setError(null);
    try {
      const { url } = await apiService.getNegotiationAttachmentUrl(message.id, idx);
      // The presigned URL already carries response-content-disposition=attachment,
      // so navigating to it triggers a download (instead of opening inline).
      // Open in a new tab to avoid clobbering chat scroll; the browser will
      // close the tab once the download starts.
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      logger.error('[AttachmentChip] download failed', err);
      setError(err?.status === 410 ? 'No longer available' : 'Download failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Pressable
      onPress={handlePress}
      disabled={!canDownload || busy}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        paddingHorizontal: 10,
        paddingVertical: 6,
        borderRadius: 6,
        backgroundColor: 'rgba(255,255,255,0.06)',
        borderWidth: 1,
        borderColor: canDownload ? 'rgba(5, 86, 247, 0.35)' : 'rgba(255,255,255,0.10)',
        opacity: busy ? 0.6 : 1,
      }}
    >
      <Icon
        name={busy ? 'progress-download' : 'paperclip'}
        size={14}
        color={canDownload ? REDESIGN_COLORS.ctaBlue : REDESIGN_COLORS.textSecondary}
      />
      <MessageContent
        $color={REDESIGN_COLORS.textPrimary}
        style={{ fontSize: 12, flex: 1 }}
        numberOfLines={1}
      >
        {filename}
      </MessageContent>
      {aiRead && (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 3 }}>
          <Icon name="text-search" size={12} color={REDESIGN_COLORS.ctaBlue} />
          <MessageContent $color={REDESIGN_COLORS.ctaBlue} style={{ fontSize: 11 }}>
            AI read it
          </MessageContent>
        </View>
      )}
      {sizeKb !== null && (
        <MessageContent $color={REDESIGN_COLORS.textSecondary} style={{ fontSize: 11 }}>
          {sizeKb} KB
        </MessageContent>
      )}
      {error && (
        <MessageContent $color={REDESIGN_COLORS.scorePoor} style={{ fontSize: 11 }}>
          {error}
        </MessageContent>
      )}
    </Pressable>
  );
};

const formatGBP = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '';
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      maximumFractionDigits: 0,
    }).format(Number(value));
  } catch {
    return `£${Number(value).toLocaleString('en-GB')}`;
  }
};

const ChatBubble = React.memo(({ message, dealerName, onWrapUpDeal, onKeepNegotiating }) => {
  const [reportOpen, setReportOpen] = useState(false);
  // Pending state for deal-prompt buttons. The worker LLM round-trip can take
  // 5-15s; without feedback users assume the click did nothing. Self-clears
  // after 60s as a safety net in case the dispatched job never produces a
  // visible state change (e.g. dedup hit, worker error).
  const [actionPending, setActionPending] = useState(null);
  const pendingTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (pendingTimerRef.current) clearTimeout(pendingTimerRef.current);
    };
  }, []);

  const triggerWithPending = async (kind, fn) => {
    if (actionPending) return;
    setActionPending(kind);
    if (pendingTimerRef.current) clearTimeout(pendingTimerRef.current);
    pendingTimerRef.current = setTimeout(() => setActionPending(null), 60000);
    try {
      await fn();
    } catch (err) {
      logger.error('[ChatBubble] deal-prompt action failed:', err);
      setActionPending(null);
    }
  };

  if (!message) return null;

  const config = ROLE_CONFIG[message.role] || DEFAULT_CONFIG;
  const statusIcon = getStatusIcon(message.status);
  const isSystemMessage = message.role === 'system';
  const isDraft = message.role === 'agent_draft';
  const isGuidedContent = isDraft && message.content?.startsWith('{');
  // Only AI-authored content is reportable. dealer_received and user_written
  // belong to other parties; system messages aren't drafts.
  const isReportable = (message.role === 'agent_draft' || message.role === 'agent_sent') && !!message.id;
  // Substitute the dealer's actual name for the generic "Dealer" label and
  // for "The dealer" / "the dealer" mentions in system message bodies.
  const trimmedDealer = (dealerName || '').trim();
  const baseLabel = isGuidedContent ? 'AI Coaching' : config.label;
  const roleLabel = trimmedDealer && DEALER_ROLES.has(message.role) ? trimmedDealer : baseLabel;

  return (
    <>
      <BubbleRow $align={config.align}>
        <BubbleContainer $bgColor={config.bgColor} $maxWidth={config.maxWidthPct}>
          {/* Role label */}
          <RoleLabel $color={config.labelColor}>{roleLabel}</RoleLabel>

          {/* Subject line */}
          {message.subject && (
            <MessageContent $color={config.textColor} style={{ fontFamily: 'Satoshi-Bold', marginBottom: 4 }}>
              {message.subject}
            </MessageContent>
          )}

          {/* Message body - guided mode vs regular */}
          {(() => {
            // Try to parse JSON content (guided coaching or dealer reported)
            let parsed = null;
            if (message.content && message.content.startsWith('{')) {
              try { parsed = JSON.parse(message.content); } catch {}
            }

            // Dealer reported structured input
            if (parsed?.outcome_type && message.role === 'dealer_reported') {
              const outcomeLabels = {
                counter_offer: 'Counter-offer',
                rejected: 'Offer rejected',
                question: 'Dealer asked a question',
                agreed: 'Deal agreed',
                noncommittal: 'No commitment from dealer',
                no_response: 'No response from dealer',
                other: 'Update',
              };
              return (
                <>
                  <MessageContent $color={config.textColor} style={{ fontFamily: 'Satoshi-Bold' }}>
                    {outcomeLabels[parsed.outcome_type] || parsed.outcome_type}
                    {parsed.counter_offer_price ? `: \u00A3${Number(parsed.counter_offer_price).toLocaleString('en-GB')}` : ''}
                  </MessageContent>
                  {parsed.summary ? (
                    <MessageContent $color={config.textColor} style={{ marginTop: 4 }}>
                      {parsed.summary}
                    </MessageContent>
                  ) : null}
                  {parsed.channel ? (
                    <MessageContent $color={REDESIGN_COLORS.textSecondary} style={{ marginTop: 4, fontSize: 12 }}>
                      Via {({ phone: 'phone call', in_person: 'in person', email: 'email', web_form: 'dealer website', other: 'other' })[parsed.channel] || parsed.channel}
                    </MessageContent>
                  ) : null}
                </>
              );
            }

            if (parsed?.coachingText) {
              return (
                <>
                  <SectionHeader>Your script</SectionHeader>
                  <MessageContent $color={config.textColor}>
                    {(parsed.coachingText || '').replace(/^[""]|[""]$/g, '').trim()}
                  </MessageContent>
                  {parsed.talkingPoints && (
                    <>
                      <SectionHeader style={{ marginTop: 16 }}>If they push back</SectionHeader>
                      <MessageContent $color={config.textColor}>
                        {parsed.talkingPoints
                          .replace(/\*\*(.+?)\*\*/g, '$1')
                          .replace(/^\*\s+/gm, '\u2022 ')
                          .replace(/^-\s+/gm, '\u2022 ')}
                      </MessageContent>
                    </>
                  )}
                </>
              );
            }

            // System messages may reference the dealer generically ("The dealer
            // appears to have accepted..."). Swap in the real name when we have it.
            let body = message.content || '';
            if (isSystemMessage && trimmedDealer) {
              body = body
                .replace(/\bThe dealer\b/g, trimmedDealer)
                .replace(/\bthe dealer\b/g, trimmedDealer);
            }
            // Inbound dealer emails arrive with the quoted thread, our worker's
            // reply-to bracket lines, the DooCars footer, and a "Re: ..." subject
            // echo. Strip all of that so only the freshly-written reply shows.
            if (message.role === 'dealer_received') {
              body = stripQuotedReply(body, { dealerName: trimmedDealer });
            }
            // Worker LLM sometimes ends its draft with `\n\n---` (separator
            // before the REASONING section). Older drafts in the DB still
            // carry that suffix; strip it client-side so the chat reads clean
            // even before the worker fix backfills.
            if (
              (message.role === 'agent_draft' || message.role === 'agent_sent') &&
              typeof body === 'string'
            ) {
              body = body.replace(/\s*\n[-*_]{3,}\s*$/m, '').trimEnd();
            }
            const dealPromptKind =
              isSystemMessage && message.structured_input?.kind === 'deal_reached_prompt';
            const agreedPrice = dealPromptKind
              ? message.structured_input?.agreedPrice ?? null
              : null;
            return (
              <>
                <MessageContent $color={config.textColor}>{body}</MessageContent>
                {dealPromptKind && (onWrapUpDeal || onKeepNegotiating) ? (
                  <View
                    style={{
                      flexDirection: 'row',
                      gap: 8,
                      marginTop: 10,
                      flexWrap: 'wrap',
                      justifyContent: 'center',
                    }}
                  >
                    {onWrapUpDeal ? (
                      <Pressable
                        accessibilityRole="button"
                        accessibilityLabel="Confirm deal and hand off"
                        onPress={() =>
                          triggerWithPending('wrap', () => onWrapUpDeal(agreedPrice))
                        }
                        disabled={!!actionPending}
                        style={({ pressed }) => ({
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: 6,
                          paddingHorizontal: 14,
                          paddingVertical: 8,
                          borderRadius: 6,
                          backgroundColor: REDESIGN_COLORS.ctaBlue,
                          opacity: actionPending && actionPending !== 'wrap' ? 0.4 : pressed ? 0.85 : 1,
                        })}
                      >
                        {actionPending === 'wrap' ? (
                          <ActivityIndicator size="small" color={REDESIGN_COLORS.textPrimary} />
                        ) : null}
                        <MessageContent
                          $color={REDESIGN_COLORS.textPrimary}
                          style={{ fontFamily: 'Satoshi-Bold', fontSize: 13 }}
                        >
                          {actionPending === 'wrap'
                            ? 'Wrapping up...'
                            : agreedPrice
                            ? `Wrap up at ${formatGBP(agreedPrice)}`
                            : 'Wrap up'}
                        </MessageContent>
                      </Pressable>
                    ) : null}
                    {onKeepNegotiating ? (
                      <Pressable
                        accessibilityRole="button"
                        accessibilityLabel="Keep negotiating"
                        onPress={() => triggerWithPending('continue', onKeepNegotiating)}
                        disabled={!!actionPending}
                        style={({ pressed }) => ({
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: 6,
                          paddingHorizontal: 14,
                          paddingVertical: 8,
                          borderRadius: 6,
                          backgroundColor: 'rgba(255,255,255,0.08)',
                          borderWidth: 1,
                          borderColor: 'rgba(255,255,255,0.16)',
                          opacity: actionPending && actionPending !== 'continue' ? 0.4 : pressed ? 0.85 : 1,
                        })}
                      >
                        {actionPending === 'continue' ? (
                          <ActivityIndicator size="small" color={REDESIGN_COLORS.textPrimary} />
                        ) : null}
                        <MessageContent
                          $color={REDESIGN_COLORS.textPrimary}
                          style={{ fontFamily: 'Satoshi-Bold', fontSize: 13 }}
                        >
                          {actionPending === 'continue' ? 'Drafting reply...' : 'Keep negotiating'}
                        </MessageContent>
                      </Pressable>
                    ) : null}
                  </View>
                ) : null}
              </>
            );
          })()}

          {/* Attachments — clickable chips. On press, the negotiation-attachment-download
              edge function re-fetches the source .eml from S3, re-parses, and
              streams just the requested attachment back as a forced download.
              "AI read it" indicator appears when extractor pulled PDF text. */}
          {Array.isArray(message.attachments) && message.attachments.length > 0 && (
            <View style={{ marginTop: 8, gap: 6 }}>
              {message.attachments.map((att, idx) => (
                <AttachmentChip key={`${att?.filename || 'att'}-${idx}`} message={message} att={att} idx={idx} />
              ))}
            </View>
          )}

          {/* Timestamp and status */}
          <MetaRow>
            <TimestampText>{formatTimestamp(message.created_at)}</TimestampText>
            {message.status === 'sent' && (
              <>
                <Icon name="check-circle" size={15} color={REDESIGN_COLORS.scoreGood} />
                <TimestampText style={{ color: REDESIGN_COLORS.scoreGood, fontFamily: 'Satoshi-Bold' }}>
                  Sent
                </TimestampText>
              </>
            )}
            {message.status === 'approved' && (
              <>
                <Icon name="check-circle-outline" size={14} color={REDESIGN_COLORS.scoreGood} />
                <TimestampText style={{ color: REDESIGN_COLORS.scoreGood }}>
                  Approved
                </TimestampText>
              </>
            )}
            {message.status === 'draft' && statusIcon && (
              <Icon name={statusIcon} size={14} color={REDESIGN_COLORS.textSecondary} />
            )}
            {message.status === 'send_failed' && (
              <>
                <Icon name="alert-circle-outline" size={14} color={REDESIGN_COLORS.scorePoor} />
                <TimestampText style={{ color: REDESIGN_COLORS.scorePoor }}>
                  Failed
                </TimestampText>
              </>
            )}
            {isReportable && (
              <Pressable
                onPress={() => setReportOpen(true)}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 3,
                  marginLeft: 8,
                  paddingVertical: 2,
                  paddingHorizontal: 4,
                }}
                hitSlop={6}
              >
                <Icon name="flag-outline" size={12} color={REDESIGN_COLORS.textSecondary} />
                <TimestampText style={{ color: REDESIGN_COLORS.textSecondary }}>
                  Report
                </TimestampText>
              </Pressable>
            )}
          </MetaRow>
        </BubbleContainer>
      </BubbleRow>

      {/* Beta footer for draft messages */}
      {isDraft && message.status === 'draft' && <BetaFooter />}

      {/* Report-draft modal — mounted only for AI messages */}
      {isReportable && (
        <ReportDraftModal
          visible={reportOpen}
          onClose={() => setReportOpen(false)}
          messageId={message.id}
        />
      )}
    </>
  );
});

ChatBubble.displayName = 'ChatBubble';

export default ChatBubble;
